/** Session GET routes: the viewer, owner console, proposals, credits, subscriptions. */
import { infiniteQueryOptions, queryOptions, useInfiniteQuery, useQuery } from '@tanstack/react-query'
import type { PerfRange } from '@algoboo/schema'
import { request } from '../client'
import { isApiError } from '../errors'
import { qk } from '../keys'
import { flushPendingSignOut } from '../signout'
import { pageOpts } from './paging'

/** The signed-in user, or `null` when signed out (401 is not an error). */
export const meQuery = () =>
  queryOptions({
    queryKey: qk.me(),
    queryFn: async ({ signal }) => {
      // a sign-out that could not reach the API is finished first; until then nobody is signed in here (V-R6)
      if (!(await flushPendingSignOut())) return null
      try {
        return await request('getMe', { signal })
      } catch (e) {
        if (isApiError(e) && (e.code === 'UNAUTHENTICATED' || e.status === 401)) return null
        throw e
      }
    },
    retry: false,
    staleTime: 60_000,
  })

export function useMe() {
  return useQuery(meQuery())
}

export const myWalletsQuery = () => queryOptions({ queryKey: qk.myWallets(), queryFn: ({ signal }) => request('listMyWallets', { signal }) })

export function useMyWallets(enabled = true) {
  return useQuery({ ...myWalletsQuery(), enabled })
}

export const myApiKeysQuery = () => queryOptions({ queryKey: qk.myApiKeys(), queryFn: ({ signal }) => request('listMyApiKeys', { signal }) })

export function useMyApiKeys(enabled = true) {
  return useQuery({ ...myApiKeysQuery(), enabled })
}

export function useFollowing(enabled = true) {
  return useQuery({ queryKey: qk.following(), queryFn: ({ signal }) => request('listMyFollowing', { signal }), enabled })
}

export const agentManageQuery = (handle: string) => queryOptions({ queryKey: qk.manage(handle), queryFn: ({ signal }) => request('getAgentManage', { signal, params: { handle } }) })

export function useAgentManage(handle: string, enabled = true) {
  return useQuery({ ...agentManageQuery(handle), enabled: enabled && !!handle })
}

export function useAgentUsage(handle: string, range: PerfRange = '7d', enabled = true) {
  return useQuery({
    queryKey: qk.manageUsage(handle, range),
    queryFn: ({ signal }) => request('getAgentManageUsage', { signal, params: { handle }, query: { range } }),
    enabled: enabled && !!handle,
  })
}

export const creditsQuery = () => queryOptions({ queryKey: qk.credits(), queryFn: ({ signal }) => request('getMyCredits', { signal }) })

export function useCredits(enabled = true) {
  return useQuery({ ...creditsQuery(), enabled })
}

export const paymentsQuery = () =>
  infiniteQueryOptions({
    queryKey: qk.payments(),
    queryFn: ({ pageParam, signal }) => request('listMyPayments', { signal, query: { cursor: pageParam, limit: 20 } }),
    ...pageOpts,
  })

export function usePayments(enabled = true) {
  return useInfiniteQuery({ ...paymentsQuery(), enabled })
}

export function usePayment(id: string | null | undefined, enabled = true) {
  return useQuery({ queryKey: qk.payment(id ?? ''), queryFn: ({ signal }) => request('getMyPayment', { signal, params: { id: id! } }), enabled: enabled && !!id })
}

export function useSubscriptions(enabled = true) {
  return useQuery({ queryKey: qk.subscriptions(), queryFn: ({ signal }) => request('listMySubscriptions', { signal }), enabled })
}

export function useClaimable(handle: string, holder: string | undefined, enabled = true) {
  return useQuery({
    queryKey: qk.claimable(handle, holder ?? ''),
    queryFn: ({ signal }) => request('getTokenClaimable', { signal, params: { handle }, query: { holder: holder! } }),
    enabled: enabled && !!handle && !!holder,
  })
}

// --- paid agent creation (docs/API.md "Agent creation") ------------------------------

/** GET /me/creation-credits: paid, consumed and refunded creation credits. */
export function useCreationCredits(enabled = true) {
  return useQuery({ queryKey: qk.creationCredits(), queryFn: ({ signal }) => request('listMyCreationCredits', { signal }), enabled })
}

